import React from 'react';
import AirportSearchInput from './AirportSearchInput';
import DatePickerInput from './DatePickerInput';
import AIRPORTS from '../data/airports';

const INTERNATIONAL_AIRPORT_SUGGESTIONS = [
  AIRPORTS.find(a => a.code === 'MEL'),
  AIRPORTS.find(a => a.code === 'SYD'),
  AIRPORTS.find(a => a.code === 'DEL'),
  AIRPORTS.find(a => a.code === 'BOM'),
  AIRPORTS.find(a => a.code === 'DPS'),
  AIRPORTS.find(a => a.code === 'BKK'),
  AIRPORTS.find(a => a.code === 'SIN'),
  AIRPORTS.find(a => a.code === 'DXB'),
].filter(Boolean);

const MIN_LEGS = 2;
const MAX_LEGS = 5;

/**
 * Multi-city search fields — rendered in place of SearchFormTailwind on the same search card
 * when tripType === 'multi-city' (Webjet-style: the tab reshapes the existing form rather than
 * navigating away). Reuses the same traveller/class panel as one-way/round-trip since passenger
 * count and cabin class aren't trip-type-specific.
 */
const MultiCityFormFields = ({
  legs,
  onLegChange,
  onAddLeg,
  onRemoveLeg,
  onSubmit,
  loading,
  todayDate,
  formData,
  travellerSummary,
  travellerPanelOpen,
  setTravellerPanelOpen,
  travellerPanelRef,
  handlePassengerCountChange,
  handleClassChange,
  classOptions,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <div className="space-y-2">
        {legs.map((leg, index) => {
          const minDate = index === 0
            ? todayDate
            : (legs[index - 1]?.date || todayDate);

          return (
            <div key={index} className="flex gap-1.5">
              <div
                className="flex items-center justify-center w-6 h-6 mt-2 rounded-full bg-primary/10 text-primary font-bold text-xs flex-shrink-0"
                aria-hidden="true"
              >
                {index + 1}
              </div>
              {/* Stacked, not side-by-side: the airport autocomplete dropdown has a fixed
                  ~340px min-width (shared with the one-way/round-trip form), and this search
                  card is capped at max-w-md (448px) even on desktop — putting two of these
                  fields in the same row means the dropdown for one spills over and covers
                  the other. The existing one-way form avoids this the same way (From above To). */}
              <div className="flex-1 space-y-1.5 min-w-0">
                <AirportSearchInput
                  id={`multicity-from-${index}`}
                  label={null}
                  value={leg.from}
                  placeholder="From"
                  onChange={(city) => onLegChange(index, 'from', city)}
                  excludeAirport={leg.to}
                  defaultSuggestions={INTERNATIONAL_AIRPORT_SUGGESTIONS}
                />
                <AirportSearchInput
                  id={`multicity-to-${index}`}
                  label={null}
                  value={leg.to}
                  placeholder="To"
                  onChange={(city) => onLegChange(index, 'to', city)}
                  excludeAirport={leg.from}
                  defaultSuggestions={INTERNATIONAL_AIRPORT_SUGGESTIONS}
                />
                <DatePickerInput
                  id={`multicity-date-${index}`}
                  name="date"
                  label={null}
                  placeholder="Date"
                  minDate={minDate}
                  value={leg.date}
                  onChange={(e) => onLegChange(index, 'date', e.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={() => onRemoveLeg(index)}
                disabled={legs.length <= MIN_LEGS}
                className="flex-shrink-0 w-7 h-7 mt-2 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-0 disabled:pointer-events-none transition-colors"
                aria-label={`Remove flight ${index + 1}`}
              >
                ✕
              </button>
            </div>
          );
        })}

        <button
          type="button"
          onClick={onAddLeg}
          disabled={legs.length >= MAX_LEGS}
          className="text-sm font-semibold text-primary hover:text-primary-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          + Add another flight
        </button>

        {/* Travellers — identical panel to SearchFormTailwind, since count/class aren't trip-type-specific */}
        <div className="relative" ref={travellerPanelRef}>
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
            Travellers &amp; Class
          </label>
          <button
            type="button"
            onClick={() => setTravellerPanelOpen(!travellerPanelOpen)}
            className="w-full px-3 py-2.5 text-left text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-primary focus:outline-none focus:border-primary transition-all flex items-center justify-between shadow-sm"
            aria-expanded={travellerPanelOpen}
          >
            <span>{travellerSummary}</span>
            <span className={`transition-transform text-gray-400 text-xs ${travellerPanelOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          {travellerPanelOpen && (
            <div className="absolute z-50 mb-2 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-full min-w-[300px] bottom-full left-0">
              <div className="space-y-3">
                {[
                  { key: 'adults', label: 'Adults', sub: '12y+' },
                ].map(({ key, label, sub }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">{label} <span className="text-xs font-normal text-gray-400">({sub})</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => handlePassengerCountChange(key, -1)}
                        className="w-8 h-8 rounded-full border border-gray-300 hover:border-primary hover:bg-blue-50 flex items-center justify-center text-gray-600 hover:text-primary font-bold transition-all">−</button>
                      <span className="w-5 text-center font-semibold text-gray-900">{formData[key]}</span>
                      <button type="button" onClick={() => handlePassengerCountChange(key, 1)}
                        className="w-8 h-8 rounded-full border border-gray-300 hover:border-primary hover:bg-blue-50 flex items-center justify-center text-gray-600 hover:text-primary font-bold transition-all">+</button>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-400">Multi-city currently supports adult passengers only.</p>

                <div className="pt-3 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Travel Class</div>
                  <div className="flex gap-1.5 flex-wrap">
                    {classOptions.map((option) => (
                      <button key={option} type="button" onClick={() => handleClassChange(option)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          formData.class === option
                            ? 'bg-primary text-white shadow-sm'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}>
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <button type="button" onClick={() => setTravellerPanelOpen(false)}
                  className="w-full py-2.5 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors shadow-sm text-sm">
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-1">
        <button type="submit" disabled={loading}
          className="w-full px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg">
          {loading ? 'Searching...' : 'Search Flights'}
        </button>
      </div>
    </form>
  );
};

export default MultiCityFormFields;
